import { useId, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { motion } from 'framer-motion'

import DDIcon from '../../icons/DDIcon'
import DropdownOverlay from './DropdownOverlay'
import useOptionListKeyDown from './useOptionListKeyDown'
import { useReducedMotionPreference, DURATION } from '../../../styles/motion'
import selectStyles from './Select.module.css'
import styles from './BankSelect.module.css'

// Spec §5.4 — bank picker: search field + rows with a 28px logo circle. Max height adapts
// to the space left below the trigger (120–260px); we clamp to that range via CSS maxHeight.
// Logo falls back to the bank's initial until the backend confirms a logo URL field exists
// (flagged in the rebuild plan's backend follow-ups).
const BankSelect = ({ label, required, placeholder, value, banks, onChange, error, disabled, loading }) => {
  const triggerRef = useRef(null)
  const panelRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const reduceMotion = useReducedMotionPreference()
  const labelId = useId()
  const helperId = `${labelId}-helper`
  const handlePanelKeyDown = useOptionListKeyDown(panelRef)

  // Spec §10 - search input autofocuses on open; ArrowDown from it moves into the list.
  const handleSearchKeyDown = (e) => {
    if (e.key !== 'ArrowDown') return
    e.preventDefault()
    e.stopPropagation()
    panelRef.current?.querySelector('[role="option"]')?.focus()
  }

  const selected = banks.find((b) => b.id === value)

  const filtered = useMemo(() => {
    if (!query.trim()) return banks
    const q = query.trim().toLowerCase()
    return banks.filter((b) => b.name.toLowerCase().includes(q))
  }, [banks, query])

  const triggerClasses = [selectStyles.trigger, open && selectStyles.open, error && selectStyles.error]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={selectStyles.wrapper}>
      {label && (
        <span className={selectStyles.label} id={labelId}>
          {label}
          {required && <span className={selectStyles.required}>*</span>}
        </span>
      )}
      <button
        type='button'
        ref={triggerRef}
        className={triggerClasses}
        disabled={disabled || loading}
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-labelledby={label ? labelId : undefined}
        aria-describedby={error ? helperId : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={[selectStyles.triggerLabel, !selected && selectStyles.placeholder].filter(Boolean).join(' ')}>
          {loading ? 'Loading banks…' : selected ? selected.name : placeholder}
        </span>
        <DDIcon name='select-caret' className={open ? selectStyles.caretOpen : undefined} />
      </button>
      {error && (
        <div id={helperId} className={selectStyles.helper}>
          <DDIcon name='helper-error' />
          <span>{error}</span>
        </div>
      )}
      <DropdownOverlay triggerRef={triggerRef} isOpen={open} onClose={() => setOpen(false)}>
        <motion.div
          ref={panelRef}
          role='listbox'
          className={selectStyles.panel}
          style={{ maxHeight: 260, minHeight: filtered.length ? undefined : 120 }}
          initial={reduceMotion ? false : { opacity: 0, y: -6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: DURATION.dropdown }}
          onKeyDown={handlePanelKeyDown}
        >
          <div className={styles.search}>
            <DDIcon name='search' />
            <input
              className={styles.searchInput}
              placeholder='Search banks'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              autoFocus
            />
          </div>
          {filtered.length === 0 && <div className={selectStyles.empty}>No banks found</div>}
          {filtered.map((bank) => (
            <button
              key={bank.id}
              type='button'
              role='option'
              aria-selected={bank.id === value}
              className={selectStyles.option}
              onClick={() => {
                onChange(bank.id)
                setQuery('')
                setOpen(false)
              }}
            >
              <span className={styles.row}>
                <span className={styles.logo}>
                  {bank.logoUrl ? (
                    <img className={styles.logoImg} src={bank.logoUrl} alt='' />
                  ) : (
                    bank.name.charAt(0).toUpperCase()
                  )}
                </span>
                {bank.name}
              </span>
            </button>
          ))}
        </motion.div>
      </DropdownOverlay>
    </div>
  )
}

BankSelect.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  banks: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string, logoUrl: PropTypes.string }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
}

BankSelect.defaultProps = {
  required: false,
  disabled: false,
  loading: false,
}

export default BankSelect
